import { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { IconSearch, IconChevronRight, Button } from '../atoms';
import { HELP_ARTICLES, HELP_ESSENTIALS, categorizedArticles, getArticle, categoryMeta, articleNeighbours } from '../help/registry';
import { useIsCompact } from '../lib/useMediaQuery';
import type { HelpCategoryId } from '../help/types';
import { scrollAncestorToTop } from '../lib/scrollContainer';
import { searchHelp } from '../help/search';
import { ArticleBody, ArticleLink } from '../help/ArticleBody';
import { startTour } from '../help/tourState';

/**
 * HelpCenter — /help and /help/:slug.
 *
 * Two screens sharing one route component:
 *   /help        → search + browse-by-category index
 *   /help/:slug  → one article
 *
 * Search runs against the in-bundle corpus (see help/search.ts), so results
 * appear on every keystroke with no request. The index is NOT a search-results
 * page with an empty query — an empty box shows the browse view, because a new
 * user who does not yet know the vocabulary cannot search for it and needs
 * something to read instead.
 */

const READING_WIDTH = 760;

/** The fields StepLink needs off an article. */
type HelpArticleRef = { slug: string; title: string; category: HelpCategoryId };

export function HelpCenter() {
  const { slug } = useParams<{ slug: string }>();
  return slug ? <HelpArticleView slug={slug} /> : <HelpIndex />;
}

/* ────────────────────────── index ────────────────────────── */

function HelpIndex() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const hits = useMemo(() => searchHelp(query, HELP_ARTICLES), [query]);
  const searching = query.trim().length > 0;
  const categories = useMemo(() => categorizedArticles(), []);
  const essentials = useMemo(
    () => HELP_ESSENTIALS.map(getArticle).filter((a): a is NonNullable<typeof a> => !!a),
    [],
  );

  return (
    <div style={{ maxWidth: READING_WIDTH }}>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Help</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
          lineHeight: 'var(--lh-normal)',
        }}>
          Ask in your own words — you do not need to know the official term.
        </p>
      </header>

      {/* ── search ── */}
      <div style={{ position: 'relative', marginBottom: 'var(--sp-5)' }}>
        <span style={{
          position: 'absolute',
          left: 'var(--sp-4)',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--color-ink-3)',
          pointerEvents: 'none',
          display: 'flex',
        }}>
          <IconSearch size={18} />
        </span>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help — try “what is a parcel” or “why can’t I see invoices”"
          aria-label="Search help articles"
          style={{
            width: '100%',
            padding: 'var(--sp-4) var(--sp-4) var(--sp-4) 46px',
            fontSize: 'var(--fs-lg)',
            fontFamily: 'var(--font-body)',
            color: 'var(--color-ink)',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 'var(--r-lg)',
            outlineOffset: 2,
          }}
        />
      </div>

      {searching ? (
        <SearchResults hits={hits} query={query} />
      ) : (
        <>
          <section style={{ marginBottom: 'var(--sp-7)' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-3)',
              marginBottom: 'var(--sp-3)',
            }}>
              <h2 style={sectionHeadingStyle}>New here? Read these six</h2>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { startTour(); navigate('/'); }}
              >Take the tour again</Button>
            </div>
            <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              {essentials.map((a) => (
                <ArticleLink key={a.slug} slug={a.slug} title={a.title} summary={a.summary} />
              ))}
            </div>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>Browse everything</h2>
            <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
              {categories.map((c) => (
                <div
                  key={c.id}
                  style={{
                    padding: 'var(--sp-4)',
                    background: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--r-lg)',
                  }}
                >
                  <div style={{
                    fontWeight: 'var(--fw-semibold)',
                    color: 'var(--color-ink)',
                    fontSize: 'var(--fs-lg)',
                  }}>{c.label}</div>
                  <div style={{
                    margin: '2px 0 var(--sp-3)',
                    color: 'var(--color-ink-3)',
                    fontSize: 'var(--fs-sm)',
                    lineHeight: 'var(--lh-normal)',
                  }}>{c.blurb}</div>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {c.articles.map((a) => (
                      <li key={a.slug}>
                        <Link
                          to={`/help/${a.slug}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--sp-2)',
                            padding: 'var(--sp-2) 0',
                            color: 'var(--brand-primary)',
                            textDecoration: 'none',
                            fontSize: 'var(--fs-md)',
                            borderTop: '1px solid var(--color-border)',
                          }}
                        >
                          <IconChevronRight size={14} />
                          {a.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function SearchResults({ hits, query }: { hits: ReturnType<typeof searchHelp>; query: string }) {
  if (hits.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-7)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        textAlign: 'center',
      }}>
        <p style={{ margin: 0, color: 'var(--color-ink-2)', fontSize: 'var(--fs-lg)' }}>
          Nothing matched “{query}”.
        </p>
        <p style={{ margin: 'var(--sp-2) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          Try fewer words, or clear the box to browse by topic.
        </p>
      </div>
    );
  }

  return (
    <section>
      <h2 style={sectionHeadingStyle}>
        {hits.length} {hits.length === 1 ? 'result' : 'results'}
      </h2>
      <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
        {hits.map((h) => (
          <Link
            key={h.article.slug}
            to={`/help/${h.article.slug}`}
            style={{
              display: 'block',
              padding: 'var(--sp-4)',
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)',
              textDecoration: 'none',
            }}
          >
            <div style={{
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--brand-primary)',
              fontSize: 'var(--fs-lg)',
            }}>{h.article.title}</div>
            <div style={{
              marginTop: 4,
              fontSize: 'var(--fs-md)',
              color: 'var(--color-ink-2)',
              lineHeight: 'var(--lh-normal)',
            }}>{h.snippet}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────── article ────────────────────────── */

function HelpArticleView({ slug }: { slug: string }) {
  const article = getArticle(slug);
  const compact = useIsCompact();
  const articleRef = useRef<HTMLElement>(null);

  // Jumping between articles must start at the top — otherwise a click on a
  // sibling link halfway down the rail opens the next article already scrolled
  // into its middle, which reads as a broken link.
  //
  // ⚠️ It is NOT `window.scrollTo(0, 0)`. The operator shell gives `<main>` its
  // own scrollbar and the window never scrolls, so that call is a silent no-op —
  // it was, here, until this was caught by watching the page not move.
  useEffect(() => { scrollAncestorToTop(articleRef.current); }, [slug]);

  if (!article) {
    return (
      <div style={{ maxWidth: READING_WIDTH }}>
        <h1 style={{ fontSize: 'var(--fs-2xl)', color: 'var(--color-ink)', margin: 0 }}>
          That help article does not exist
        </h1>
        <p style={{ color: 'var(--color-ink-2)', fontSize: 'var(--fs-md)' }}>
          It may have been renamed. <Link to="/help" style={{ color: 'var(--brand-primary)' }}>Search the Help Center</Link> instead.
        </p>
      </div>
    );
  }

  const category = categoryMeta(article.category);
  const { prev, next, siblings, prevCrossesSection, nextCrossesSection } = articleNeighbours(slug);
  const related = (article.related ?? [])
    .map(getArticle)
    .filter((a): a is NonNullable<typeof a> => !!a);

  // The list of everything in this section, current entry marked. On desktop it
  // is a sticky rail beside the article; on a narrow screen it drops underneath,
  // because a 260px rail next to a 320px article is neither.
  const sectionList = (
    <nav aria-label={`More in ${category?.label ?? 'this section'}`}>
      <div style={{
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--color-ink-3)',
        marginBottom: 'var(--sp-3)',
      }}>{category?.label ?? 'In this section'}</div>

      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {siblings.map((a) => {
          const isCurrent = a.slug === article.slug;
          return (
            <li key={a.slug}>
              <Link
                to={`/help/${a.slug}`}
                aria-current={isCurrent ? 'page' : undefined}
                style={{
                  display: 'block',
                  padding: 'var(--sp-2) var(--sp-3)',
                  marginBottom: 2,
                  borderLeft: `2px solid ${isCurrent ? 'var(--brand-primary)' : 'transparent'}`,
                  borderRadius: 'var(--r-sm)',
                  background: isCurrent ? 'var(--brand-primary-bg)' : 'transparent',
                  color: isCurrent ? 'var(--brand-primary)' : 'var(--color-ink-2)',
                  fontWeight: isCurrent ? 'var(--fw-semibold)' : 'var(--fw-normal)',
                  fontSize: 'var(--fs-md)',
                  lineHeight: 'var(--lh-normal)',
                  textDecoration: 'none',
                }}
              >{a.title}</Link>
            </li>
          );
        })}
      </ul>

      <Link
        to="/help"
        style={{
          display: 'inline-block',
          marginTop: 'var(--sp-4)',
          paddingLeft: 'var(--sp-3)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-medium)',
          color: 'var(--brand-primary)',
          textDecoration: 'none',
        }}
      >Browse all topics →</Link>
    </nav>
  );

  const articleColumn = (
    <article ref={articleRef} style={{ maxWidth: READING_WIDTH, minWidth: 0 }}>
      {/* Trail back out. Two rungs, both clickable, so "how do I get back to the
          list I came from" has an answer that is visible without scrolling. */}
      <nav
        aria-label="Breadcrumb"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-ink-3)',
          flexWrap: 'wrap',
        }}
      >
        <Link to="/help" style={{ color: 'var(--brand-primary)', textDecoration: 'none', fontWeight: 'var(--fw-medium)' }}>
          All help
        </Link>
        {category && (
          <>
            <span aria-hidden="true">›</span>
            <span>{category.label}</span>
          </>
        )}
      </nav>

      <h1 style={{
        margin: '0 0 var(--sp-2)',
        fontSize: 'var(--fs-3xl)',
        fontWeight: 'var(--fw-semibold)',
        letterSpacing: 'var(--ls-tight)',
        lineHeight: 'var(--lh-tight)',
        color: 'var(--color-ink)',
      }}>{article.title}</h1>

      <p style={{
        margin: '0 0 var(--sp-6)',
        fontSize: 'var(--fs-xl)',
        lineHeight: 'var(--lh-normal)',
        color: 'var(--color-ink-3)',
      }}>{article.summary}</p>

      <ArticleBody blocks={article.body} />

      {/* Step to the next article without going back to an index first. */}
      {(prev || next) && (
        <nav
          aria-label="Previous and next article"
          style={{
            display: 'flex',
            gap: 'var(--sp-3)',
            marginTop: 'var(--sp-7)',
            paddingTop: 'var(--sp-5)',
            borderTop: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {prev ? <StepLink article={prev} direction="prev" crossesSection={prevCrossesSection} /> : <span style={{ flex: 1 }} />}
          {next ? <StepLink article={next} direction="next" crossesSection={nextCrossesSection} /> : <span style={{ flex: 1 }} />}
        </nav>
      )}

      {related.length > 0 && (
        <section style={{ marginTop: 'var(--sp-6)' }}>
          <h2 style={sectionHeadingStyle}>Read next</h2>
          <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
            {related.map((a) => (
              <ArticleLink key={a.slug} slug={a.slug} title={a.title} summary={a.summary} />
            ))}
          </div>
        </section>
      )}

      {/* On a narrow screen the rail lives here instead — same list, same links,
          just after the thing you were reading rather than beside it. */}
      {compact && (
        <section style={{
          marginTop: 'var(--sp-7)',
          paddingTop: 'var(--sp-5)',
          borderTop: '1px solid var(--color-border)',
        }}>{sectionList}</section>
      )}
    </article>
  );

  if (compact) return articleColumn;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `minmax(0, ${READING_WIDTH}px) 260px`,
      gap: 'var(--sp-7)',
      alignItems: 'start',
    }}>
      {articleColumn}
      <aside style={{ position: 'sticky', top: 'var(--sp-5)' }}>{sectionList}</aside>
    </div>
  );
}

/** One half of the prev/next pair at the foot of an article. */
function StepLink({ article, direction, crossesSection }: {
  article: HelpArticleRef;
  direction: 'prev' | 'next';
  crossesSection: boolean;
}) {
  const isNext = direction === 'next';
  // Name the section when the step leaves this one. Landing in a different
  // subject with the label still reading a bare "Next" is the disorienting part.
  const label = crossesSection
    ? `${isNext ? 'Next section' : 'Previous section'} · ${categoryMeta(article.category)?.label ?? ''}`
    : (isNext ? 'Next' : 'Previous');
  return (
    <Link
      to={`/help/${article.slug}`}
      style={{
        flex: 1,
        minWidth: 200,
        padding: 'var(--sp-3) var(--sp-4)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        background: 'var(--color-card)',
        textDecoration: 'none',
        textAlign: isNext ? 'right' : 'left',
      }}
    >
      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{label}</div>
      <div style={{
        marginTop: 2,
        fontWeight: 'var(--fw-medium)',
        color: 'var(--brand-primary)',
        fontSize: 'var(--fs-md)',
        lineHeight: 'var(--lh-normal)',
      }}>{article.title}</div>
    </Link>
  );
}

const sectionHeadingStyle: React.CSSProperties = {
  margin: '0 0 var(--sp-3)',
  fontSize: 'var(--fs-md)',
  fontWeight: 'var(--fw-semibold)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--color-ink-3)',
};
